import { useState, useCallback } from 'react';

/**
 * Hook personalizado para manejar la selección de versículos bíblicos
 * Soporta:
 * - Click simple (toggle individual)
 * - Shift + Click (selección de rango)
 * - Mouse drag (arrastrar para seleccionar)
 * 
 * @returns {Object} - Estado y funciones de selección
 */
export const useVerseSelection = () => {
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [lastSelectedVerse, setLastSelectedVerse] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Limpiar selección
   */
  const clearSelection = useCallback(() => {
    setSelectedVerses([]);
    setLastSelectedVerse(null);
    setIsDragging(false);
  }, []);

  /**
   * Manejar click en un versículo
   * - Click simple: toggle individual
   * - Shift + Click: selección de rango
   */
  const handleVerseClick = useCallback((verseNumber, isShiftPressed = false) => {
    if (isShiftPressed && lastSelectedVerse !== null) {
      // Selección de rango con Shift
      const start = Math.min(lastSelectedVerse, verseNumber);
      const end = Math.max(lastSelectedVerse, verseNumber);
      const rangeVerses = [];
      for (let i = start; i <= end; i++) {
        rangeVerses.push(i);
      }
      
      setSelectedVerses(prev => {
        const newSelection = new Set([...prev, ...rangeVerses]);
        return Array.from(newSelection).sort((a, b) => a - b);
      });
    } else {
      // Toggle individual
      setSelectedVerses(prev => {
        if (prev.includes(verseNumber)) {
          return prev.filter(v => v !== verseNumber);
        } else {
          return [...prev, verseNumber].sort((a, b) => a - b);
        }
      });
      setLastSelectedVerse(verseNumber);
    }
  }, [lastSelectedVerse]);

  /**
   * Iniciar arrastre
   */
  const handleDragStart = useCallback((verseNumber) => {
    setIsDragging(true);
    setLastSelectedVerse(verseNumber);
    // Agregar el versículo inicial a la selección si no está
    setSelectedVerses(prev => {
      if (!prev.includes(verseNumber)) {
        return [...prev, verseNumber].sort((a, b) => a - b);
      }
      return prev;
    });
  }, []);

  /**
   * Arrastrar sobre versículos
   */
  const handleDragOver = useCallback((verseNumber) => {
    if (!isDragging) return;
    
    setSelectedVerses(prev => {
      if (!prev.includes(verseNumber)) {
        return [...prev, verseNumber].sort((a, b) => a - b);
      }
      return prev;
    });
  }, [isDragging]);

  /**
   * Terminar arrastre
   */
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Generar referencia bíblica legible
   */
  const getSelectionReference = useCallback((book, chapter) => {
    if (selectedVerses.length === 0) return '';
    
    if (selectedVerses.length === 1) {
      return `${book} ${chapter}:${selectedVerses[0]}`;
    }
    
    // Si son consecutivos, mostrar como rango
    const isConsecutive = selectedVerses.every((v, i, arr) => 
      i === 0 || v === arr[i - 1] + 1
    );
    
    if (isConsecutive) {
      return `${book} ${chapter}:${selectedVerses[0]}-${selectedVerses[selectedVerses.length - 1]}`;
    }
    
    // Si no son consecutivos, mostrar lista
    return `${book} ${chapter}:${selectedVerses.join(', ')}`;
  }, [selectedVerses]);

  return {
    selectedVerses,
    handleVerseClick,
    clearSelection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getSelectionReference,
    isDragging,
    hasSelection: selectedVerses.length > 0,
    selectionCount: selectedVerses.length
  };
};
